import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { formatScore } from '../../utils/scoreFormat';
import AccessWarningModal from '../common/AccessWarningModal';
import TestAnalytics from '../Analytics/TestAnalytics';
import TestResultBreakdown from './TestResultBreakdown';
import MessageThread from '../Messages/MessageThread';
import { useFeatureConfig } from '../../services/featureConfigContext';
import '../Results/Results.css';

function formatClosedBy(role) {
  if (role === 'user') return 'пользователем';
  if (role === 'psychologist') return 'вами (психологом)';
  return role || '—';
}

/** Стабильный ключ группы: один тест = одна группа даже при смене названия в справочнике. */
function getTestGroupKey(result) {
  const id = result.testId ?? result.testSnapshot?.originalTestId;
  if (id != null && String(id).trim() !== '') {
    return `id:${String(id)}`;
  }
  const title = result.testSnapshot?.title?.trim();
  return title ? `title:${title}` : 'unknown';
}

function compareResultDatesAsc(a, b) {
  const ta = a?.date ? new Date(a.date).getTime() : 0;
  const tb = b?.date ? new Date(b.date).getTime() : 0;
  return ta - tb;
}

function PsychologistUserProfilePage() {
  const { features } = useFeatureConfig();
  const { userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const resultsTab = searchParams.get('tab') === 'analytics' && features.analytics
    ? 'analytics'
    : 'overview';

  useEffect(() => {
    if (!features.analytics && searchParams.get('tab') === 'analytics') {
      setSearchParams({});
    }
  }, [features.analytics, searchParams, setSearchParams]);

  const [user, setUser] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resultsError, setResultsError] = useState('');
  const [expandedResultId, setExpandedResultId] = useState(null);
  const [resultsGroupByTest, setResultsGroupByTest] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [notesError, setNotesError] = useState('');

  const hasApprovedAccess = user?.accessStatus === 'approved';

  const loadUser = async () => {
    const users = await api.getPsychologistUsers();
    const foundUser = users.find((item) => String(item._id) === String(userId));
    if (!foundUser) {
      throw new Error('Пользователь не найден в списке психолога');
    }
    setUser(foundUser);
    return foundUser;
  };

  const loadResults = async (targetUser = user) => {
    if (!targetUser || targetUser.accessStatus !== 'approved') {
      setResults([]);
      setResultsError('Для просмотра результатов нужен подтвержденный доступ');
      return;
    }
    try {
      setResultsLoading(true);
      setResultsError('');
      const data = await api.getPsychologistUserResults(userId);
      setResults(data);
    } catch (err) {
      setResults([]);
      setResultsError(err.message || 'Не удалось загрузить результаты пользователя');
    } finally {
      setResultsLoading(false);
    }
  };

  const loadNotes = async (targetUser = user) => {
    if (!targetUser?.isAssignedToMe && targetUser?.accessStatus !== 'approved') {
      setNotes([]);
      return;
    }
    try {
      setNotesLoading(true);
      setNotesError('');
      const data = await api.getPsychologistUserNotes(userId);
      setNotes(data);
    } catch (err) {
      setNotes([]);
      setNotesError(err.message || 'Не удалось загрузить заметки');
    } finally {
      setNotesLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        setError('');
        const loadedUser = await loadUser();
        await Promise.all([loadResults(loadedUser), loadNotes(loadedUser)]);
      } catch (err) {
        setError(err.message || 'Не удалось открыть профиль пользователя');
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [userId]);

  const latestResult = useMemo(() => (results.length > 0 ? results[0] : null), [results]);

  const resultsGroupedByTest = useMemo(() => {
    const map = new Map();
    for (const r of results) {
      const key = getTestGroupKey(r);
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(r);
    }
    const groups = [...map.entries()].map(([key, items]) => {
      const sorted = [...items].sort(compareResultDatesAsc);
      const last = sorted[sorted.length - 1];
      const title = last?.testSnapshot?.title || items[0]?.testSnapshot?.title || 'Неизвестный тест';
      const category = last?.testSnapshot?.category || items[0]?.testSnapshot?.category || '—';
      return { key, title, category, attempts: sorted };
    });
    groups.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
    return groups;
  }, [results]);

  const handleAssign = async () => {
    await api.assignPsychologistUser(userId);
    const u = await loadUser();
    await Promise.all([loadResults(u), loadNotes(u)]);
  };

  const openRequestAccess = () => {
    setDialog({
      type: 'request',
      title: 'Запросить доступ к материалам',
      warning:
        'Пользователь получит запрос в личном кабинете. Доступ к результатам тестов возможен только после явного согласия пользователя.',
      confirmLabel: 'Отправить запрос',
      confirmButtonClass: 'btn-success',
      showOptionalMessage: true,
      optionalMessageLabel: 'Сообщение пользователю (необязательно)',
    });
  };

  const openCloseAccess = () => {
    setDialog({
      type: 'close',
      title: 'Закрыть доступ',
      warning:
        'Вы прекращаете доступ к материалам этого пользователя. Пользователь увидит причину закрытия. Для повторного просмотра потребуется новое одобрение.',
      confirmLabel: 'Закрыть доступ',
      confirmButtonClass: 'btn-danger',
      reasonRequired: true,
      reasonLabel: 'Причина закрытия',
      reasonPlaceholder: 'Эта причина будет доступна пользователю.',
    });
  };

  const openReleaseClient = () => {
    setDialog({
      type: 'release',
      title: 'Освободить клиента',
      warning:
        'Вы завершаете работу с этим пользователем. Назначение будет снято, но доступ к материалам (если был одобрен) сохранится до его закрытия.',
      confirmLabel: 'Освободить',
      confirmButtonClass: 'btn-warning',
      reasonRequired: true,
      reasonLabel: 'Причина завершения работы',
      reasonPlaceholder: 'Например: завершение терапии, передача другому специалисту.',
    });
  };

  const handleModalConfirm = async ({ reason, optionalMessage }) => {
    if (dialog.type === 'request') {
      await api.requestMaterialsAccess(userId, {
        scope: ['tests'],
        messageFromPsychologist: optionalMessage,
      });
    } else if (dialog.type === 'close') {
      await api.closeMaterialsAccess(user.accessRequestId, reason);
    } else if (dialog.type === 'release') {
      await api.releasePsychologistUser(userId, reason);
    }
    const u = await loadUser();
    await Promise.all([loadResults(u), loadNotes(u)]);
  };

  const handleCreateNote = async (event) => {
    event.preventDefault();
    if (!noteText.trim()) {
      return;
    }
    try {
      await api.createPsychologistUserNote(userId, noteText.trim());
      setNoteText('');
      await loadNotes(user);
    } catch (err) {
      setNotesError(err.message || 'Не удалось сохранить заметку');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.deletePsychologistUserNote(userId, noteId);
      await loadNotes(user);
    } catch (err) {
      setNotesError(err.message || 'Не удалось удалить заметку');
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="alert alert-info">Загрузка профиля пользователя...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
        <Link to="/psychologist/users" className="ui-btn ui-btn-ghost">
          Назад к пользователям
        </Link>
      </div>
    );
  }

  const showClosureDetails =
    user?.accessClosedAt &&
    (user.accessStatus === 'revoked_by_user' || user.accessStatus === 'closed_by_psychologist');

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h2 className="mb-0">Профиль пользователя для психолога</h2>
        <Link to="/psychologist/users" className="ui-btn ui-btn-ghost">
          Назад к пользователям
        </Link>
      </div>

      <div className="ui-card p-3 mb-3">
        <h5 className="mb-2">{user?.username || 'Неизвестный пользователь'}</h5>
        <div className="text-muted">{user?.email || 'email не указан'}</div>
        <div className="d-flex gap-2 flex-wrap mt-3 align-items-center">
          <span className="badge bg-secondary">В работе: {user?.assignmentStatus || 'unassigned'}</span>
          <span className="badge bg-info">Доступ: {user?.accessStatus || 'no_request'}</span>
        </div>

        <div className="d-flex flex-wrap gap-2 mt-3">
          {!user?.isAssignedToMe && (
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleAssign}>
              Взять в работу
            </button>
          )}
          {user?.isAssignedToMe && user?.assignmentStatus === 'in_progress' && (
            <button type="button" className="btn btn-sm btn-outline-warning" onClick={openReleaseClient}>
              Освободить клиента
            </button>
          )}
          {user?.accessStatus !== 'approved' && (
            <button type="button" className="btn btn-sm btn-outline-success" onClick={openRequestAccess}>
              Запросить доступ
            </button>
          )}
          {user?.accessStatus === 'approved' && user?.accessRequestId && (
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={openCloseAccess}>
              Закрыть доступ
            </button>
          )}
        </div>

        {showClosureDetails && (
          <div className="alert alert-light border mt-3 mb-0">
            <strong className="d-block mb-1">Сведения о закрытии доступа</strong>
            <div className="small text-muted">Дата: {new Date(user.accessClosedAt).toLocaleString('ru-RU')}</div>
            <div className="small text-muted">Инициатор: {formatClosedBy(user.accessClosedByRole)}</div>
            {user.accessCloseReason && <div className="small mt-2">{user.accessCloseReason}</div>}
          </div>
        )}
      </div>

      {(user?.isAssignedToMe || hasApprovedAccess) && (
        <div className="ui-card p-3 mb-3">
          <h4 className="mb-3">Заметки о клиенте</h4>
          <p className="text-muted small">Заметки видны только вам и не отображаются пользователю.</p>
          {notesError && <div className="alert alert-danger py-2">{notesError}</div>}
          {notesLoading ? (
            <p className="text-muted">Загрузка заметок…</p>
          ) : notes.length === 0 ? (
            <p className="text-muted">Заметок пока нет.</p>
          ) : (
            <ul className="list-group mb-3">
              {notes.map((note) => (
                <li className="list-group-item d-flex justify-content-between align-items-start gap-2" key={note._id}>
                  <div>
                    <div className="small text-muted mb-1">
                      {new Date(note.createdAt).toLocaleString('ru-RU')}
                    </div>
                    <div>{note.text}</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDeleteNote(note._id)}
                  >
                    Удалить
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={handleCreateNote}>
            <label htmlFor="psychologist-note" className="form-label">Новая заметка</label>
            <textarea
              id="psychologist-note"
              className="form-control mb-2"
              rows={3}
              value={noteText}
              onChange={(event) => setNoteText(event.target.value)}
              placeholder="Наблюдения, контекст работы, план сессий"
              maxLength={5000}
            />
            <button type="submit" className="btn btn-sm btn-primary" disabled={!noteText.trim()}>
              Сохранить заметку
            </button>
          </form>
        </div>
      )}

      {features.messaging && (
        <div className="ui-card p-3 mb-3">
          <h4 className="mb-3">Сообщения с клиентом</h4>
          <MessageThread userId={userId} />
        </div>
      )}

      <div className="ui-card p-3 mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="mb-0">Результаты тестов</h4>
          {hasApprovedAccess && (
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => loadResults()}>
              Обновить результаты
            </button>
          )}
        </div>

        {!hasApprovedAccess && (
          <div className="alert alert-warning mt-3 mb-0">
            Для просмотра результатов нужно, чтобы пользователь одобрил доступ. Используйте кнопку «Запросить доступ» выше — откроется окно с пояснением.
          </div>
        )}

        {hasApprovedAccess && resultsError && <div className="alert alert-danger mt-3">{resultsError}</div>}

        {hasApprovedAccess && !resultsError && (
          <>
            <div className="ui-card d-flex gap-2 p-2 mt-3 mb-0">
              <button
                type="button"
                className={`ui-btn ${resultsTab === 'overview' ? 'ui-btn-primary' : 'ui-btn-ghost'}`}
                onClick={() => setSearchParams({})}
              >
                Обзор
              </button>
              {features.analytics && (
                <button
                  type="button"
                  className={`ui-btn ${resultsTab === 'analytics' ? 'ui-btn-primary' : 'ui-btn-ghost'}`}
                  onClick={() => setSearchParams({ tab: 'analytics' })}
                >
                  Аналитика
                </button>
              )}
            </div>

            {resultsTab === 'analytics' ? (
              <div className="mt-2">
                <TestAnalytics
                  embedded
                  resultsOverride={results}
                  overrideLoading={resultsLoading}
                  overrideError=""
                  analyticsContext="client"
                  clientUserId={userId}
                />
              </div>
            ) : (
              <>
                {resultsLoading && <div className="alert alert-info mt-3">Загрузка результатов...</div>}
                {!resultsLoading && (
                  <>
                    {latestResult && (
                      <div className="alert alert-light mt-3">
                        Последний тест: <strong>{latestResult.testSnapshot?.title || '—'}</strong>, балл{' '}
                        {formatScore(latestResult.score)}
                      </div>
                    )}

                    {results.length === 0 ? (
                      <div className="alert alert-secondary mt-3 mb-0">У пользователя пока нет результатов.</div>
                    ) : (
                      <>
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-3 mb-2">
                          <span className="small text-muted me-1">Показ:</span>
                          <button
                            type="button"
                            className={`btn btn-sm ${resultsGroupByTest ? 'btn-outline-secondary' : 'btn-secondary'}`}
                            onClick={() => setResultsGroupByTest(false)}
                          >
                            Все прохождения по дате
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${resultsGroupByTest ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            onClick={() => setResultsGroupByTest(true)}
                          >
                            По тесту (динамика во времени)
                          </button>
                        </div>
                        {resultsGroupByTest ? (
                          <div className="d-flex flex-column gap-3 mt-1">
                            {resultsGroupedByTest.map((group) => (
                              <div key={group.key} className="border rounded p-3 bg-light bg-opacity-25">
                                <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-2">
                                  <div>
                                    <h6 className="mb-0">{group.title}</h6>
                                    <div className="small text-muted">
                                      {group.category} · прохождений: {group.attempts.length}
                                    </div>
                                  </div>
                                  <div className="small text-muted">
                                    Внутри группы — от раннего к позднему (как менялись баллы и уровень)
                                  </div>
                                </div>
                                <div className="table-responsive">
                                  <table className="table table-sm table-hover align-middle mb-0">
                                    <thead>
                                      <tr>
                                        <th>№</th>
                                        <th>Дата</th>
                                        <th>Балл</th>
                                        <th>Уровень</th>
                                        <th>Действия</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {group.attempts.map((result, idx) => (
                                        <Fragment key={result._id}>
                                          <tr>
                                            <td className="text-muted">{idx + 1}</td>
                                            <td>
                                              {result.date ? new Date(result.date).toLocaleString('ru-RU') : '—'}
                                            </td>
                                            <td>{formatScore(result.score)}</td>
                                            <td>{result.interpretation?.level || '—'}</td>
                                            <td>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() =>
                                                  setExpandedResultId((current) =>
                                                    current === result._id ? null : result._id
                                                  )
                                                }
                                              >
                                                {expandedResultId === result._id ? 'Скрыть детали' : 'Детали теста'}
                                              </button>
                                            </td>
                                          </tr>
                                          {expandedResultId === result._id && (
                                            <tr>
                                              <td colSpan={5}>
                                                <TestResultBreakdown result={result} />
                                              </td>
                                            </tr>
                                          )}
                                        </Fragment>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="table-responsive mt-1">
                            <table className="table table-hover align-middle">
                              <thead>
                                <tr>
                                  <th>Тест</th>
                                  <th>Категория</th>
                                  <th>Балл</th>
                                  <th>Уровень</th>
                                  <th>Дата</th>
                                  <th>Действия</th>
                                </tr>
                              </thead>
                              <tbody>
                                {results.map((result) => (
                                  <Fragment key={result._id}>
                                    <tr>
                                      <td>{result.testSnapshot?.title || 'Неизвестный тест'}</td>
                                      <td>{result.testSnapshot?.category || '—'}</td>
                                      <td>{formatScore(result.score)}</td>
                                      <td>{result.interpretation?.level || '—'}</td>
                                      <td>{result.date ? new Date(result.date).toLocaleString('ru-RU') : '—'}</td>
                                      <td>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={() =>
                                            setExpandedResultId((current) =>
                                              current === result._id ? null : result._id
                                            )
                                          }
                                        >
                                          {expandedResultId === result._id ? 'Скрыть детали' : 'Детали теста'}
                                        </button>
                                      </td>
                                    </tr>
                                    {expandedResultId === result._id && (
                                      <tr>
                                        <td colSpan={6}>
                                          <TestResultBreakdown result={result} />
                                        </td>
                                      </tr>
                                    )}
                                  </Fragment>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      <AccessWarningModal
        open={Boolean(dialog)}
        onClose={() => setDialog(null)}
        title={dialog?.title || ''}
        warning={dialog?.warning || ''}
        reasonRequired={dialog?.reasonRequired || false}
        reasonLabel={dialog?.reasonLabel}
        reasonPlaceholder={dialog?.reasonPlaceholder}
        showOptionalMessage={dialog?.showOptionalMessage || false}
        optionalMessageLabel={dialog?.optionalMessageLabel}
        confirmLabel={dialog?.confirmLabel || 'Подтвердить'}
        confirmButtonClass={dialog?.confirmButtonClass || 'btn-primary'}
        onConfirm={handleModalConfirm}
      />
    </div>
  );
}

export default PsychologistUserProfilePage;
